import { useCan, useGetIdentity, usePermissions } from "@refinedev/core";
import { Box, Card, CardContent, Typography, CircularProgress } from "@mui/material";
import type { TaruviUser } from "../../providers/refineProviders";
import { extractRoleKeys, isAdminFromRoleKeys } from "../../utils/roleAccess";
import { getEmployeeIdFromIdentity, getRolesFromIdentity } from "../../utils/userIdentity";

export const AccessGate = ({ children }: { children: React.ReactNode }) => {
  const { data: user, isLoading: identityLoading } = useGetIdentity<TaruviUser>();
  const { data: permissions, isLoading: permissionsLoading } = usePermissions<any>({});
  const { data: canListEmployees, isLoading: canLoading } = useCan({
    resource: "datatable:employees",
    action: "read",
  });

  // Only block on the initial load, not background refetches
  if (identityLoading || permissionsLoading || canLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  const permissionRoleKeys = extractRoleKeys(permissions?.roles);
  const identityRoleKeys = extractRoleKeys(getRolesFromIdentity(user));
  const roleKeys = [...permissionRoleKeys, ...identityRoleKeys];
  const isAdminByRole = isAdminFromRoleKeys(roleKeys);
  const isAdminByFlags = Boolean((permissions as any)?.is_staff || (permissions as any)?.is_superuser);
  const isAdminByPolicy = Boolean(canListEmployees?.can);
  const isAdmin = isAdminByRole || isAdminByFlags || isAdminByPolicy;
  const employeeId = getEmployeeIdFromIdentity(user);
  const hasEmployeeId = Boolean(employeeId);

  if (isAdmin || hasEmployeeId) {
    return <>{children}</>;
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh", p: 3 }}>
      <Card sx={{ maxWidth: 520, width: "100%" }}>
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="h5" fontWeight={700}>No Employee Record Linked</Typography>
          <Typography color="text.secondary">
            Your account is not linked to an employee record. Please contact your HR administrator to set up your profile.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};
